import { Navigate, useLocation } from "react-router-dom";

import { useIntegratorMe } from "@/lib/me-query";
import { appsVisibleInAppSwitcher } from "@/lib/subapp-access";
import { hubModuleHasAnyAccess } from "@/lib/subapp-access";
import {
  isFloorTabletDevice,
  parseFloorTabletHomeQuery,
  readFloorTabletHomePreference,
  writeFloorTabletHomePreference,
} from "@/lib/floorTabletDevice";

import { FloorTabletHomePage } from "@/pages/FloorTabletHomePage";

function NoModuleAccess() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md rounded-lg border border-amber-200 bg-white p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-slate-900">No modules assigned</h1>
        <p className="mt-2 text-sm text-slate-600">
          Your account does not have access to any integrator module yet. Ask a Smart Grid Solutions
          integrator administrator to enable at least one module for your role, or to confirm your{" "}
          <span className="font-mono">user_setting</span> role and organisation are correct.
        </p>
      </div>
    </div>
  );
}

function userHasFloorModuleAccess(me: ReturnType<typeof useIntegratorMe>["data"]): boolean {
  return hubModuleHasAnyAccess(me, "manufacturing") || hubModuleHasAnyAccess(me, "shipping");
}

export function shouldOpenFloorTabletHome(me: ReturnType<typeof useIntegratorMe>["data"]): boolean {
  if (!userHasFloorModuleAccess(me)) {
    return false;
  }
  const preference = readFloorTabletHomePreference();
  if (preference === "on") {
    return true;
  }
  if (preference === "off") {
    return false;
  }
  return isFloorTabletDevice();
}

/** Send the user to the floor tablet home or the personal home dashboard. */
export function AppRedirect() {
  const { search } = useLocation();
  const { data: me } = useIntegratorMe();
  const allowed = appsVisibleInAppSwitcher(me);

  const floorQuery = parseFloorTabletHomeQuery(search);
  if (floorQuery === "on") {
    writeFloorTabletHomePreference("on");
  } else if (floorQuery === "off") {
    writeFloorTabletHomePreference("off");
  }

  if (allowed.length === 0) {
    return <NoModuleAccess />;
  }

  if (floorQuery === "off") {
    return <Navigate to="/personal/home" replace />;
  }

  if (shouldOpenFloorTabletHome(me) || floorQuery === "on") {
    return <FloorTabletHomePage />;
  }

  return <Navigate to="/personal/home" replace />;
}

/** Old MRP deep links and bookmarks: `/mrp/bom?…` → `/bom?…` with the same query string. */
export function MrpBomToBomAppRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/bom", search }} replace />;
}
